import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faSlidersH } from "@fortawesome/pro-solid-svg-icons/faSlidersH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Badge from "antd/es/badge";
import { Col, Row } from "antd/es/grid";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import Role from "@mapmycustomers/shared/enum/Role";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { SearchField } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import SelectUsersDialog from "@app/component/SelectEntityDialog/SelectUsersDialog";
import {
  selectUser,
  setDisabledUserIds,
  setHiddenUserIds,
} from "@app/scene/map/store/layers/userLocations/actions";
import {
  getDisabledUserIds,
  getHiddenUserIds,
  getSelectedUser,
} from "@app/scene/map/store/layers/userLocations/selectors";
import { getMe } from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import { isManager, isMember, isReadOnly } from "@app/store/iam/util";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import layout from "@app/styles/layout";
import Iam from "@app/types/Iam";
import useAnalytics from "@app/util/contexts/useAnalytics";
import defaultIdGetter from "@app/util/defaultIdGetter";
import { userDisplayName } from "@app/util/formatters";
import isUserActive from "@app/util/map/isUserActive";

import UserLocation from "../UserLocation";

import styles from "./UserLocationList.module.scss";

interface Props {
  disabledUserIds: User["id"][];
  hiddenUserIds: User["id"][];
  me: Iam;
  onUpdateMetadata: typeof updateMetadata.request;
  selectedUser?: User;
  selectUser: typeof selectUser;
  setDisabledUserIds: typeof setDisabledUserIds;
  setHiddenUserIds: typeof setHiddenUserIds;
  users: User[];
}

const UserLocationList: React.FC<Props> = ({
  disabledUserIds,
  hiddenUserIds,
  me,
  onUpdateMetadata,
  selectedUser,
  selectUser,
  setDisabledUserIds,
  setHiddenUserIds,
  users,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [userModalVisible, showUserModal, hideUserModal] = useBoolean();
  const [usersOnList, setUsersOnList] = useState<User[]>(
    users.filter(({ id }) => !disabledUserIds.includes(id))
  );

  useChangeTracking(() => {
    analytics.clicked(["Select users"], { users: usersOnList.map(({ username }) => username) });
    const usersOnListIds = new Set(usersOnList.map(({ id }) => id));
    const mapLegendUserLocationsDisabledUserIds = users
      .filter(({ id }) => !usersOnListIds.has(id))
      .map(({ id }) => id);
    setDisabledUserIds(mapLegendUserLocationsDisabledUserIds);
    onUpdateMetadata({ mapLegendUserLocationsDisabledUserIds });
    if (selectedUser && mapLegendUserLocationsDisabledUserIds.includes(selectedUser.id)) {
      selectUser(undefined);
    }
  }, [analytics, selectedUser, selectUser, setDisabledUserIds, users, usersOnList]);

  const [search, setSearch] = useState<string>("");

  const allowedTeamIds: Set<Team["id"]> | undefined = useMemo(() => {
    return isManager(me) ? new Set(me.teams.map(({ id }) => id)) : undefined;
  }, [me]);

  const allowedRoles: Set<Role> | undefined = useMemo(() => {
    return isManager(me) ? new Set([Role.MANAGER, Role.MEMBER]) : undefined;
  }, [me]);

  const allowedUsers = useMemo(() => {
    return usersOnList.filter(
      ({ id, role, teams }) =>
        (!allowedTeamIds || teams.some(({ id }) => allowedTeamIds.has(id))) &&
        (!allowedRoles || allowedRoles.has(role.key)) &&
        ((!isMember(me) && !isReadOnly(me)) || id === me.id)
    );
  }, [allowedTeamIds, allowedRoles, me, usersOnList]);

  const filteredUsers = useMemo(() => {
    const searchText = search.toLowerCase().trim();
    return allowedUsers
      .filter(
        (user) =>
          searchText.length === 0 || userDisplayName(user).toLowerCase().includes(searchText)
      )
      .sort(
        (a, b) =>
          (isUserActive(a) ? 0 : 1) - (isUserActive(b) ? 0 : 1) ||
          userDisplayName(a).localeCompare(userDisplayName(b))
      );
  }, [allowedUsers, search]);

  const getUserSelectorIcon = useCallback((allSelected: boolean) => {
    if (allSelected) {
      return <FontAwesomeIcon icon={faSlidersH} />;
    }
    return (
      <Badge color={colors.primary100} dot>
        <FontAwesomeIcon icon={faSlidersH} />
      </Badge>
    );
  }, []);

  const handleChangeUserVisibility = useCallback(
    (userId: User["id"], visibility: boolean) => {
      const mapLegendUserLocationsHiddenUserIds = visibility
        ? hiddenUserIds.filter((id) => id !== userId)
        : [...hiddenUserIds, userId];
      setHiddenUserIds(mapLegendUserLocationsHiddenUserIds);
      onUpdateMetadata({ mapLegendUserLocationsHiddenUserIds });
    },
    [hiddenUserIds, onUpdateMetadata, setHiddenUserIds]
  );

  const handleSelectUser = useCallback(
    (userId: User["id"]) => {
      setHiddenUserIds(filteredUsers.map(({ id }) => id).filter((id) => id !== userId));
    },
    [setHiddenUserIds, filteredUsers]
  );

  return (
    <div className={styles.container}>
      <Row align="middle" gutter={layout.spacerS} justify="space-between">
        <Col flex={1}>
          <SearchField
            onChange={setSearch}
            placeholder={intl.formatMessage({
              id: "map.layer.userLocation.legend.searchUsers.placeholder",
              defaultMessage: "Search Users",
              description: "Placeholder for search user input of the User Location legend",
            })}
            value={search}
          />
        </Col>
        <Col>
          <Tooltip
            title={intl.formatMessage({
              id: "map.layer.userLocation.legend.selectUser.tooltip",
              defaultMessage: "Select Teams and Users",
              description: "Tooltip for user select button on the User Location legend",
            })}
          >
            <ButtonLink className={styles.userSelector} onClick={showUserModal}>
              {getUserSelectorIcon(usersOnList.length === users.length)}
            </ButtonLink>
          </Tooltip>
        </Col>
      </Row>
      <div className={styles.userContainer}>
        {filteredUsers.map((user) => (
          <UserLocation
            key={user.id}
            onChangeUserVisibility={(visibility) => handleChangeUserVisibility(user.id, visibility)}
            onSelectUser={handleSelectUser}
            shown={!hiddenUserIds.includes(user.id)}
            user={user}
          />
        ))}
      </div>
      <SelectUsersDialog
        entities={users}
        getDisplayName={userDisplayName}
        getKey={defaultIdGetter}
        onConfirm={setUsersOnList}
        onHide={hideUserModal}
        selectedEntities={usersOnList}
        title={intl.formatMessage({
          id: "home.cards.recordsBySource.selectUsersDialog.title",
          defaultMessage: "Select Users",
          description: "Title for select users dialog",
        })}
        visible={userModalVisible}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  disabledUserIds: getDisabledUserIds(state),
  hiddenUserIds: getHiddenUserIds(state),
  me: getMe(state)!,
  selectedUser: getSelectedUser(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  onUpdateMetadata: updateMetadata.request,
  selectUser,
  setDisabledUserIds,
  setHiddenUserIds,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserLocationList);
